import type { ReactNode } from 'react';
import { Badge, cn } from '@eombor/ui';

export interface SegmentedOption<Value extends string> {
  value: Value;
  label: string;
  icon?: ReactNode;
  /**
   * Qizil son — shu ko'rinishda kimnidir ISH kutyapti. Nol yoki yo'q bo'lsa
   * chizilmaydi (yon menyudagi belgi bilan bir xil qoida).
   */
  badge?: number;
  /** Ekran o'quvchi uchun son matni — «3 ta yangi bildirgi». */
  badgeLabel?: string;
}

export interface SegmentedProps<Value extends string> {
  /** Guruhning nomi — ekran o'quvchi uchun ("Ko'rinish"). */
  label: string;
  value: Value;
  options: readonly SegmentedOption<Value>[];
  onChange: (value: Value) => void;
  className?: string;
}

/**
 * Bir obyektning ikki-uch ko'rinishi orasida almashish (segment boshqaruvi).
 *
 * `role="tablist"` EMAS, oddiy tugmalar guruhi: bosilganda manzil o'zgaradi
 * va sahifa qayta chiziladi — bu tab emas, navigatsiya. `aria-pressed` esa
 * qaysi ko'rinish yoqilganini ekran o'quvchiga aytadi.
 *
 * Ro'yxatga faqat RUXSAT ETILGAN ko'rinishlar beriladi: chizilmagan segment
 * "menda yo'q" degani, o'chirilgan segment esa "shu yerda nimadir bor edi"
 * deb odamni qidiruvga soladi.
 */
export const Segmented = <Value extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: SegmentedProps<Value>) => (
  <div
    role="group"
    aria-label={label}
    className={cn(
      'inline-flex h-[var(--size-control)] items-center gap-1 rounded-[var(--radius-sm)]',
      'border border-border bg-surface-2 p-1',
      className,
    )}
  >
    {options.map((option) => {
      const active = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(option.value)}
          className={cn(
            'flex h-full items-center gap-1.5 rounded-[var(--radius-sm)] px-3',
            'text-sm font-medium whitespace-nowrap',
            'transition-[background-color,color] duration-[var(--motion-fast)]',
            'ease-[var(--ease-standard)]',
            active
              ? 'bg-surface text-text shadow-[var(--shadow-1)]'
              : 'text-text-muted hover:text-text',
          )}
        >
          {option.icon}
          {option.label}
          {option.badge !== undefined && option.badge > 0 ? (
            <Badge tone="danger" mono aria-label={option.badgeLabel}>
              {option.badge}
            </Badge>
          ) : null}
        </button>
      );
    })}
  </div>
);
